import React, { useState } from "react";
import { Loader2, Plus, Upload, Camera } from "lucide-react";
import { api } from "@/lib/apiClient";
import { logError } from "@/lib/logger";
import { OrderDetailTable, emptyDetail } from "@/components/OrderDetailTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

export const JobOrderForm = ({ clients, onSaved }) => {
  const [form, setForm] = useState({
    tanggal: today(),
    jo_number: "",
    client_name: "",
    client_phone: "",
    deskripsi: "",
    kategori: [],
    kategori_status: {},
    status: "MASUK",
  });
  const [proof, setProof] = useState(null);
  const [details, setDetails] = useState([emptyDetail()]);
  const [photo, setPhoto] = useState(null);
  const [katPhotos, setKatPhotos] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickClient = (name) => {
    const c = clients.find((x) => x.name === name);
    setForm((f) => ({ ...f, client_name: name, client_phone: c ? c.phone : f.client_phone }));
  };

  const loadNumber = React.useCallback(async () => {
    try {
      const { data } = await api.get("/job-orders/next-number");
      setForm((f) => (f.jo_number === data.nomor ? f : { ...f, jo_number: data.nomor }));
    } catch (error) {
      logError("Gagal mengambil nomor JO berikutnya:", error);
    }
  }, []);

  React.useEffect(() => {
    loadNumber();
    const timer = setInterval(loadNumber, 10000);
    window.addEventListener("focus", loadNumber);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", loadNumber);
    };
  }, [loadNumber]);

  React.useEffect(() => {
    const list = [...new Set(details.map((d) => d.kategori).filter(Boolean))];
    setForm((f) =>
      f.kategori.join("|") === list.join("|")
        ? f
        : { ...f, kategori: list, kategori_status: Object.fromEntries(list.map((k) => [k, "MASUK"])) }
    );
  }, [details]);

  const detailSummary = () =>
    details
      .filter((d) => d.deskripsi.trim() || d.kategori)
      .map((d) => {
        const ukuran = d.panjang || d.lebar ? ` ${d.panjang || "-"}x${d.lebar || "-"}` : "";
        const jenis = d.jenis_order ? ` (${d.jenis_order})` : "";
        return `${d.deskripsi || d.kategori}${jenis}${ukuran} · ${d.qty || 1} pcs`;
      })
      .join("; ");


  const submit = async (e) => {
    e.preventDefault();
    const summary = detailSummary();
    const deskripsi = [summary, form.deskripsi.trim()].filter(Boolean).join(" | ");
    if (!form.client_name.trim() || !deskripsi) {
      toast.error("Nama client dan minimal satu baris detail order wajib diisi");
      return;
    }
    if (form.kategori.length === 0) {
      toast.error("Pilih kategori pada baris detail order");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/job-orders", {
        ...form,
        deskripsi,
        details: details
          .filter((d) => d.kategori)
          .map(({ key, ...d }) => d),
      });
      if (proof) {
        const fd = new FormData();
        fd.append("file", proof);
        await api.post(`/job-orders/${data.id}/proof`, fd);
      }
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        await api.post(`/job-orders/${data.id}/photo`, fd);
      }
      for (const [k, file] of Object.entries(katPhotos)) {
        if (!file) continue;
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/job-orders/${data.id}/items/${encodeURIComponent(k)}/photo`, fd);
      }
      toast.success(`Job Order ${data.jo_number} tersimpan`);
      if (form.jo_number && data.jo_number !== form.jo_number) {
        toast.info(
          `Nomor ${form.jo_number} sudah dipakai pengguna lain — JO ini otomatis memakai nomor ${data.jo_number}`
        );
      }
      if (data?.notification) {
        if (data.notification.sent) toast.success("Notifikasi WhatsApp terkirim ke client");
        else toast.warning("Notifikasi WA otomatis belum aktif — gunakan tombol WA di daftar JO");
      }
      setForm({ tanggal: today(), jo_number: "", client_name: "", client_phone: "", deskripsi: "", kategori: [], kategori_status: {}, status: "MASUK" });
      setProof(null);
      setDetails([emptyDetail()]);
      setPhoto(null);
      setKatPhotos({});
      loadNumber();
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan job order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="glass rounded-2xl p-6 md:p-8" data-testid="jo-form">
      <h3 className="font-display text-xl font-semibold">Entri Job Order Baru</h3>
      <p className="mt-2 text-xs text-slate-400">
        Semua kategori otomatis berstatus{" "}
        <span className="text-amber-300 font-semibold">MASUK</span> dan JO masuk ke{" "}
        <span className="text-blue-300 font-semibold">Papan Admin</span> untuk ACC sebelum diproduksi.
      </p>
      <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">Tanggal</Label>
          <Input
            data-testid="jo-date-input"
            type="date"
            value={form.tanggal}
            onChange={(e) => set("tanggal", e.target.value)}
            className="mt-2 h-11 bg-slate-900/70 border-white/10"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">
            Nomor JO (otomatis)
          </Label>
          <Input
            data-testid="jo-number-input"
            value={form.jo_number}
            readOnly
            className="mt-2 h-11 bg-slate-950/80 border-white/10 text-blue-300 font-display"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">Nama Client</Label>
          <Input
            data-testid="jo-client-input"
            list="client-list"
            placeholder="Nama client"
            value={form.client_name}
            onChange={(e) => pickClient(e.target.value)}
            className="mt-2 h-11 bg-slate-900/70 border-white/10"
          />
          <datalist id="client-list">
            {clients.map((c) => (
              <option key={c.id} value={c.name} />
            ))}
          </datalist>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">No. HP Client</Label>
          <Input
            data-testid="jo-phone-input"
            placeholder="08xxxxxxxxxx"
            value={form.client_phone}
            onChange={(e) => set("client_phone", e.target.value)}
            className="mt-2 h-11 bg-slate-900/70 border-white/10"
          />
        </div>
        <div className="md:col-span-2 lg:col-span-3">
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">
            Detail Order <span className="text-blue-400">(kategori otomatis mengikuti baris)</span>
          </Label>
          <div className="mt-3">
            <OrderDetailTable rows={details} onChange={setDetails} />
          </div>
          {form.kategori.length > 0 && (
            <div className="mt-5 rounded-xl border border-white/10 bg-slate-950/60 p-4" data-testid="jo-kategori-status-list">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                Foto awal tiap kategori (opsional) · semua kategori mulai dengan status{" "}
                <span className="text-amber-300 font-semibold">MASUK</span>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {form.kategori.map((k) => (
                  <div
                    key={k}
                    className="rounded-lg border border-white/5 bg-slate-900/70 px-3 py-2.5 space-y-2.5"
                  >
                    <div className="flex items-center gap-3">
                      <span className="min-w-[92px] rounded-md border border-blue-400/25 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-200 text-center">
                        {k}
                      </span>
                      <span
                        data-testid={`jo-kategori-status-${k.toLowerCase().replace(/\s/g, "-")}`}
                        className="text-[10px] font-semibold uppercase tracking-wider text-amber-300"
                      >
                        MASUK
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <label
                        data-testid={`jo-kategori-photo-${k.toLowerCase().replace(/\s/g, "-")}`}
                        className="inline-flex items-center gap-1.5 text-xs text-blue-300 hover:text-blue-200 cursor-pointer transition-colors duration-200"
                      >
                        <Upload className="w-3.5 h-3.5" /> Foto
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          className="hidden"
                          onChange={(e) =>
                            setKatPhotos((s) => ({ ...s, [k]: e.target.files?.[0] || null }))
                          }
                        />
                      </label>
                      <label
                        data-testid={`jo-kategori-camera-${k.toLowerCase().replace(/\s/g, "-")}`}
                        className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 cursor-pointer transition-colors duration-200"
                      >
                        <Camera className="w-3.5 h-3.5" /> Kamera
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          className="hidden"
                          onChange={(e) =>
                            setKatPhotos((s) => ({ ...s, [k]: e.target.files?.[0] || null }))
                          }
                        />
                      </label>
                      <span className="text-[10px] text-slate-500 truncate flex-1">
                        {katPhotos[k]?.name || "belum ada foto"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="md:col-span-2 lg:col-span-3">
          <Label className="text-xs uppercase tracking-[0.18em] text-slate-400">
            Catatan Tambahan (opsional)
          </Label>
          <Textarea
            data-testid="jo-desc-input"
            rows={2}
            placeholder="Catatan khusus untuk produksi, mis. bahan flexi china, finishing mata ayam"
            value={form.deskripsi}
            onChange={(e) => set("deskripsi", e.target.value)}
            className="mt-2 bg-slate-900/70 border-white/10"
          />
        </div>
      </div>
      <Button
        type="submit"
        data-testid="jo-submit-btn"
        disabled={saving}
        className="mt-7 h-12 rounded-xl bg-blue-600 hover:bg-blue-500 px-8 font-semibold lift"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" /> Simpan Job Order</>}
      </Button>
    </form>
  );
};
